import { ChevronDown, Search } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { twMerge } from 'tailwind-merge'
import { buildPickedLocation, type PickedLocation, writePickedLocation } from '../location'
import { ZipCombobox } from './zip-combobox'

type Selected = { zip: string; city: string | null; state: string | null }

type LocationPickerProps = {
  /** Trigger content — the location label as the surrounding copy renders it. */
  children: ReactNode
  /** Classes for the trigger button, so the hero and the results h1 keep their own type scale. */
  className?: string | undefined
  /**
   * Called after the pick is persisted. The results page uses it to navigate to
   * the new location path; the home page needs nothing, since useGeo re-renders
   * the hero and the search form's hidden inputs from the store.
   */
  onConfirm?: ((picked: PickedLocation) => void) | undefined
  'data-testid'?: string | undefined
}

/**
 * "Select your location" modal (VIT-17740). The visitor types a ZIP code; the
 * pick is persisted by `location.ts` and overrides CloudFront's IP-derived geo
 * everywhere `useGeo` is read.
 *
 * A native `<dialog>` opened with `showModal()`: the browser supplies the
 * backdrop, the focus trap, Escape, and the accessibility tree. `closedby="any"`
 * adds native light dismiss, so none of the manual hit-testing in the health
 * app's picker is needed; where it isn't supported the dialog simply requires
 * Escape or Cancel.
 *
 * The "Use current location" row is hidden until an endpoint reverse-geocodes a
 * point to a ZIP (VIT-17931) — see use-current-position.ts.
 */
export const LocationPicker = ({
  children,
  className = undefined,
  onConfirm = undefined,
  'data-testid': dataTestId,
}: LocationPickerProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  // Draft: nothing is persisted until Confirm, so Cancel and light dismiss both
  // leave the stored location untouched.
  const [draftZip, setDraftZip] = useState<Selected | null>(null)
  // The dialog is portalled to <body>, which can't happen during SSR — and must
  // not happen on the first client render either, or hydration would mismatch.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setPortalTarget(document.body)
  }, [])

  // Reset the draft whenever the dialog closes — by Cancel, Escape, light
  // dismiss or Confirm — so reopening never shows a stale half-selection.
  // biome-ignore lint/correctness/useExhaustiveDependencies: portalTarget is the trigger, not a value read — the dialog element (and so dialogRef.current) only exists once the portal has mounted.
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    const handleClose = () => {
      setDraftZip(null)
    }
    dialog.addEventListener('close', handleClose)
    return () => {
      dialog.removeEventListener('close', handleClose)
    }
    // Re-run once the portal exists — that's when the dialog element does.
  }, [portalTarget])

  const handleConfirm = () => {
    const picked = buildPickedLocation({
      postalCode: draftZip?.zip,
      city: draftZip?.city,
      region: draftZip?.state,
    })
    // A failed write (storage disabled by policy) still navigates — the ZIP is
    // in the URL, so the search is correct even when the pick can't persist.
    writePickedLocation(picked)
    onConfirm?.(picked)
  }

  return (
    <>
      <button
        type="button"
        className={twMerge('inline-flex cursor-pointer items-center gap-1', className)}
        onClick={() => dialogRef.current?.showModal()}
        data-testid={dataTestId}
      >
        {children}
        <ChevronDown aria-hidden="true" className="size-5 shrink-0" />
      </button>

      {/* Portalled to <body>, not rendered in place: the results trigger sits
          inside the <h1>, which permits phrasing content only — a nested dialog
          would be invalid markup and its copy would count towards the heading's
          text and accessible name. It also escapes the hero's `invisible`/`inert`
          wrapper, which would otherwise suppress the dialog while geo settles. */}
      {portalTarget != null &&
        createPortal(
          <dialog
            ref={dialogRef}
            // Native light dismiss (click the backdrop). Ignored where unsupported,
            // which leaves Escape and Cancel.
            closedby="any"
            className="dialog-panel"
            aria-labelledby={`${dataTestId ?? 'location-picker'}-title`}
            data-testid={dataTestId != null ? `${dataTestId}-dialog` : 'location-picker-dialog'}
          >
            <div className="space-y-4">
              <h2 id={`${dataTestId ?? 'location-picker'}-title`} className="font-display text-3xl font-normal">
                Select your location
              </h2>

              {/* The design labels this "enter your address", but the ticket is
              explicit that it's a ZIP code — that's what the search takes. */}
              <div className="relative">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute top-1/2 left-4 z-10 size-5 -translate-y-1/2 text-secondary-500"
                />
                <ZipCombobox
                  name="postalCode"
                  label="ZIP code"
                  // Visually unlabelled by design, so the label is hidden rather
                  // than dropped (docs/front-end/forms.md).
                  labelClassName="sr-only"
                  // showModal() autofocuses this input, so its ring is always on
                  // show — use the palette one the search form's controls use rather
                  // than the UA's blue default.
                  inputClassName="h-12 rounded-full border-transparent bg-secondary-100 pr-10 pl-11 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                  placeholder="Enter your ZIP code"
                  value={draftZip}
                  onChange={setDraftZip}
                  data-testid="location-picker-zip"
                />
              </div>

              {/* method="dialog": Cancel closes with no handler, and Confirm's submit
              closes the dialog after its onClick has committed the pick. */}
              <form method="dialog" className="grid grid-cols-2 gap-3 pt-2">
                <button type="submit" className="btn justify-center" data-testid="location-picker-cancel">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary justify-center disabled:cursor-default disabled:opacity-50"
                  disabled={draftZip == null}
                  onClick={handleConfirm}
                  data-testid="location-picker-confirm"
                >
                  Confirm
                </button>
              </form>
            </div>
          </dialog>,
          portalTarget,
        )}
    </>
  )
}
